import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import API from "@/lib/api";
import { toast } from "@/lib/notify";
import {
  Building2, UserCog, Users, Plus, Search, ArrowRight, TrendingUp,
} from "lucide-react";

const STATUS_STYLES = {
  draft: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  closed: "bg-red-500/10 text-red-400 border-red-500/20",
};

export default function TychiMaster() {
  const [funds, setFunds] = useState([]);
  const [stats, setStats] = useState({ total_funds: 0, total_admins: 0, total_investors: 0, total_users: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const loadData = useCallback(async () => {
    try {
      const [fundsRes, statsRes] = await Promise.all([
        API.get("/funds"),
        API.get("/dashboard/stats"),
      ]);
      setFunds(fundsRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filtered = funds.filter((f) => {
    // Trim leading/trailing whitespace before matching.
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return f.name?.toLowerCase().includes(q) ||
           f.fund_type?.toLowerCase().includes(q);
  });

  const statCards = [
    { title: "Total Funds", value: stats.total_funds, icon: Building2, color: "text-blue-500 dark:text-blue-400", bg: "bg-blue-500/10 border-blue-500/20" },
    { title: "Active Funds", value: funds.filter(f => f.status === "active").length, icon: TrendingUp, color: "text-emerald-500 dark:text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" },
    { title: "Fund Managers", value: stats.total_admins, icon: UserCog, color: "text-violet-500 dark:text-violet-400", bg: "bg-violet-500/10 border-violet-500/20" },
    { title: "Investors", value: stats.total_investors, icon: Users, color: "text-amber-500 dark:text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" },
  ];

  return (
    <div className="space-y-8 animate-fade-in" data-testid="tychi-master-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Tychi Master</h1>
          <p className="text-muted-foreground text-sm mt-1">Central command — manage funds and proceed into applications</p>
        </div>
        <Button asChild className="shadow-[0_0_20px_rgba(108,71,196,0.2)]" data-testid="create-fund-btn">
          <Link to="/super-admin/funds/create"><Plus className="w-4 h-4 mr-2" /> Create Fund</Link>
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card, i) => (
          <Card key={card.title} className={`stat-card animate-slide-up stagger-${i + 1} ${card.bg}`} data-testid={`stat-${card.title.toLowerCase().replace(/\s+/g, "-")}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{card.title}</span>
                <card.icon className={`w-4 h-4 ${card.color}`} strokeWidth={1.5} />
              </div>
              <p className="font-heading text-2xl font-bold">
                {loading ? <span className="inline-block w-12 h-7 bg-secondary rounded animate-pulse" /> : card.value}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Funds Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-lg font-semibold">Funds</h2>
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search funds..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-secondary/50 h-9"
              data-testid="fund-search-input"
            />
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6"><div className="h-32 bg-secondary/30 rounded" /></CardContent>
              </Card>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-3" strokeWidth={1} />
              <p className="text-muted-foreground text-sm">
                {search ? "No funds match your search" : "No funds created yet. Upload a PPM to get started."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((fund) => (
              <Card
                key={fund.id}
                className="group hover:border-primary/30 transition-all duration-200 hover:shadow-[0_0_30px_rgba(108,71,196,0.08)]"
                data-testid={`fund-card-${fund.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-primary" strokeWidth={1.5} />
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold text-base leading-tight">{fund.name}</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">{fund.fund_type || "—"}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className={`text-[10px] uppercase tracking-wider shrink-0 ${STATUS_STYLES[fund.status]}`}>
                      {fund.status}
                    </Badge>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-5">
                    <div className="p-2.5 rounded-md bg-secondary/30">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Managers</p>
                      <p className="text-sm font-semibold mt-0.5">{fund.admin_ids?.length || 0}</p>
                    </div>
                    <div className="p-2.5 rounded-md bg-secondary/30">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Reporting</p>
                      <p className="text-sm font-semibold mt-0.5 capitalize">{fund.reporting_frequency || "—"}</p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      className="flex-1 border border-border text-xs"
                      onClick={() => navigate(`/super-admin/funds/${fund.id}`)}
                      data-testid={`fund-details-${fund.id}`}
                    >
                      Details
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1 text-xs shadow-[0_0_15px_rgba(108,71,196,0.15)] group-hover:shadow-[0_0_20px_rgba(108,71,196,0.25)] transition-shadow"
                      onClick={() => navigate(`/super-admin/funds/${fund.id}/apps`)}
                      data-testid={`fund-proceed-${fund.id}`}
                    >
                      Proceed <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
